import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import type { IvaPeriodSide } from "@fiscalo/shared-types";

import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { LoadingPage } from "@/components/shared/LoadingPage";
import { PageHeader } from "@/components/shared/PageHeader";
import { BoletoPagoCard } from "@/components/features/accountant/BoletoPagoCard";
import { getAccountantIvaPeriodDetail } from "@/services/dashboardService";

const MONTH_LABELS_ES = [
  "enero",
  "febrero",
  "marzo",
  "abril",
  "mayo",
  "junio",
  "julio",
  "agosto",
  "septiembre",
  "octubre",
  "noviembre",
  "diciembre",
];

function periodLabel(period: string): string {
  const [year, month] = period.split("-");
  return `${MONTH_LABELS_ES[Number(month) - 1] ?? month} ${year}`;
}

/** YYYY-MM-DD → DD/MM/YYYY (Uruguay numeric format), timezone-safe. */
function uyDate(iso: string): string {
  const [y, m, d] = (iso ?? "").slice(0, 10).split("-");
  return y && m && d ? `${d}/${m}/${y}` : iso;
}

function money(n: number): string {
  return n.toLocaleString("es-UY", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function AccountantClientIvaPeriodPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { tenantId, period } = useParams<{ tenantId: string; period: string }>();

  useEffect(() => {
    const main = document.querySelector("main");
    if (main) main.scrollTo({ top: 0, behavior: "smooth" });
    else window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const detailQuery = useQuery({
    queryKey: ["accountant", "tenant-iva-period", tenantId ?? "", period ?? ""],
    queryFn: () => getAccountantIvaPeriodDetail(tenantId!, period!),
    enabled: Boolean(tenantId && period),
    staleTime: 60_000,
  });

  if (detailQuery.isLoading) return <LoadingPage />;

  const detail = detailQuery.data;
  if (!detail) {
    return (
      <div className="space-y-4 fx-content">
        <PageHeader
          eyebrow={t("accountantIvaPeriod.eyebrow", "IVA del período")}
          title={period ? periodLabel(period) : ""}
          onBack={() => navigate(`/accountant/client/${tenantId}`)}
        />
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            {t(
              "accountantIvaPeriod.loadError",
              "No pudimos cargar el IVA de este período.",
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  const { company, debito, credito } = detail;

  return (
    <div className="space-y-4 fx-content">
      <PageHeader
        eyebrow={t("accountantIvaPeriod.eyebrow", "IVA del período")}
        title={periodLabel(detail.period)}
        description={t(
          "accountantIvaPeriod.description",
          "Comprobantes que componen el IVA del período. Montos en UYU.",
        )}
        onBack={() => navigate(`/accountant/client/${tenantId}`)}
      />

      {/* Summary hero — débito / crédito / a pagar */}
      <div className="relative overflow-hidden rounded-[18px] bg-[var(--fx-ink-900)] px-5 py-5 text-[var(--fx-paper)] shadow-[0_18px_44px_-28px_rgba(22,21,20,0.30)] sm:px-7 sm:py-6">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(55% 80% at 100% 0%, rgba(111,165,133,0.22), transparent 60%), radial-gradient(35% 60% at 0% 100%, rgba(111,165,133,0.10), transparent 60%)",
          }}
        />
        <div className="relative flex flex-wrap items-start justify-between gap-4">
          <div className="grid grid-cols-3 gap-6">
            <IvaStat
              label={t("accountantIvaPeriod.debito", "IVA débito")}
              value={debito.totalIvaUyu}
            />
            <IvaStat
              label={t("accountantIvaPeriod.credito", "IVA crédito")}
              value={credito.totalIvaUyu}
            />
            <IvaStat
              label={t("accountantIvaPeriod.aPagar", "A pagar")}
              value={detail.aPagarUyu}
              highlight
            />
          </div>
          <div className="text-right text-xs text-[var(--fx-paper)]/55">
            <div className="font-medium text-[var(--fx-paper)]/80">
              {company.legalName?.trim() || company.name}
            </div>
            <div>RUC {company.rut}</div>
            <div className="mt-1">
              {uyDate(detail.from)} — {uyDate(detail.to)}
            </div>
          </div>
        </div>
      </div>

      <BoletoPagoCard
        tenantId={tenantId!}
        obligationId={detail.obligationId}
        aPagarUyu={detail.aPagarUyu}
        boleto={detail.boleto}
        onGenerated={async () => {
          await detailQuery.refetch();
        }}
      />

      <IvaSideTable
        title={t("accountantIvaPeriod.ventas", "Ventas (débito)")}
        counterpartyLabel={t("accountantIvaPeriod.cliente", "Cliente")}
        side={debito}
        emptyText={t(
          "accountantIvaPeriod.ventasEmpty",
          "Sin ventas confirmadas en el período.",
        )}
      />
      <IvaSideTable
        title={t("accountantIvaPeriod.compras", "Compras (crédito)")}
        counterpartyLabel={t("accountantIvaPeriod.proveedor", "Proveedor")}
        side={credito}
        emptyText={t(
          "accountantIvaPeriod.comprasEmpty",
          "Sin compras registradas en el período.",
        )}
      />
    </div>
  );
}

function IvaStat({
  label,
  value,
  highlight,
}: {
  label: string;
  value: number;
  highlight?: boolean;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <span
        className="text-[9.5px] font-bold uppercase text-[var(--fx-accent-300)]"
        style={{ fontFamily: "var(--fx-ff-mono)", letterSpacing: "0.14em" }}
      >
        {label}
      </span>
      <span
        className={
          "text-lg font-medium leading-none tabular-nums sm:text-2xl " +
          (highlight ? "text-[var(--fx-paper)]" : "text-[var(--fx-paper)]/80")
        }
        style={{ fontFamily: "var(--fx-ff-mono)" }}
      >
        $U {money(value)}
      </span>
    </div>
  );
}

function IvaSideTable({
  title,
  counterpartyLabel,
  side,
  emptyText,
}: {
  title: string;
  counterpartyLabel: string;
  side: IvaPeriodSide;
  emptyText: string;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <Card>
      <CardContent className="py-5">
        <h3 className="mb-3 text-sm font-semibold text-[var(--fx-ink-900)]">
          {title}
        </h3>
        {side.rows.length === 0 ? (
          <p className="py-3 text-sm text-muted-foreground">{emptyText}</p>
        ) : (
          <div className="overflow-x-auto">
            <Table className="min-w-[760px] text-xs">
              <TableHeader>
                <TableRow>
                  <TableHead>{t("accountantIvaPeriod.col.fecha", "Fecha")}</TableHead>
                  <TableHead>{t("accountantIvaPeriod.col.desc", "Descripción")}</TableHead>
                  <TableHead>{t("accountantIvaPeriod.col.serie", "Serie")}</TableHead>
                  <TableHead className="text-right">{t("accountantIvaPeriod.col.numero", "Número")}</TableHead>
                  <TableHead>{t("accountantIvaPeriod.col.rut", "RUT")}</TableHead>
                  <TableHead>{counterpartyLabel}</TableHead>
                  <TableHead className="text-right">{t("accountantIvaPeriod.col.neto", "Neto")}</TableHead>
                  <TableHead className="text-right">{t("accountantIvaPeriod.col.iva", "IVA")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {side.rows.map((r, i) => (
                  <TableRow key={`${r.cfeType}-${r.serie}-${r.number ?? i}`}>
                    <TableCell className="whitespace-nowrap">{uyDate(r.date)}</TableCell>
                    <TableCell className="whitespace-nowrap">{r.cfeTypeLabel}</TableCell>
                    <TableCell>{r.serie}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.number ?? ""}</TableCell>
                    <TableCell className="whitespace-nowrap">{r.counterpartyRut ?? ""}</TableCell>
                    <TableCell className="max-w-[200px] truncate">{r.counterpartyName}</TableCell>
                    <TableCell className="text-right tabular-nums">{money(r.netoUyu)}</TableCell>
                    <TableCell className="text-right tabular-nums">{money(r.ivaUyu)}</TableCell>
                  </TableRow>
                ))}
                <TableRow className="border-t-2 border-[var(--fx-ink-200)] bg-[var(--fx-paper-2)]/40 font-semibold">
                  <TableCell colSpan={6}>{t("accountantIvaPeriod.total", "TOTAL")}</TableCell>
                  <TableCell className="text-right tabular-nums">{money(side.totalNetoUyu)}</TableCell>
                  <TableCell className="text-right tabular-nums">{money(side.totalIvaUyu)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
